import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import {
    CircularProgress, TextField, Button, MenuItem, Typography, Grid, Box
} from '@mui/material';

import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useErrorManager from "../../hooks/useErrorManager";

import PageContainer from '../Layout/PageContainer';
import { useNotification } from '../../contexts/NotificationContext';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext'; // Import breadcrumb context


const StyledFormTextField = styled(TextField)`
  margin-top: 4px;
  margin-bottom: 4px;
`;


function CreateForwardingAssignment() {
    const [assignment, setAssignment] = useState({
        profileId: '',
        locationGroupId: '',
        locationId: '',
        gatewayId: '',
        exclusiveTaskType: null,
    });

    const [loading, setLoading] = useState(false);
    const [dropdownData, setDropdownData] = useState({
        profiles: [],
        locationGroups: [],
        locations: [],
        gateways: []
    });

    const { setError, clearError, getError } = useErrorManager();
    const { showNotification } = useNotification();
    const { updateBreadcrumb } = useBreadcrumb();

    const navigate = useNavigate();
    const axiosInstance = useAxiosPrivate();

    const handleChange = async (field, value) => {
        setAssignment(prevState => ({
            ...prevState,
            [field]: value,
        }));
    
        try {
            if (field === 'locationGroupId') {
                // Fetch locations and gateways for the selected group
                // const [locationsRes, gatewaysRes] = await Promise.all([
                //     axiosInstance.get(`/locations/list?groupId=${value}`),
                //     axiosInstance.get(`/gateways/list?groupId=${value}`)
                // ]);
    
                // setDropdownData(prev => ({
                //     ...prev,
                //     locations: locationsRes.data,
                //     gateways: gatewaysRes.data,
                // }));
    
                // // Reset dependent fields
                // setAssignment(prev => ({
                //     ...prev,
                //     locationId: '',
                //     gatewayId: '',
                // }));
            } else if (field === 'locationId') {
                // Fetch gateways for the selected location
                const gatewaysRes = await axiosInstance.get(`/gateways/list/byLocation/${value}`);
                setDropdownData(prev => ({
                    ...prev,
                    gateways: gatewaysRes.data,
                }));
    
                // Update group based on the location
                const location = dropdownData.locations.find(loc => loc.id === value);
                if (location) {
                    setAssignment(prev => ({
                        ...prev,
                        locationGroupId: location.parentId,
                        gatewayId: '',
                    }));
                }
            } else if (field === 'gatewayId') {
                // Update location and group based on the gateway
                const gateway = dropdownData.gateways.find(gw => gw.id === value);
                if (gateway) {
                    setAssignment(prev => ({
                        ...prev,
                        locationId: gateway.locationId,
                        locationGroupId: gateway.locationDetails.parentId,
                    }));
                }
            }
        } catch (error) {
            setError('fetch', error.message);
        }
    };


    useEffect(() => {
        const fetchInitialData  = async () => {
            try {;
                setLoading(true);
                const [profilesRes, locationGroupsRes, locationsRes, gatewaysRes] = await Promise.all([
                    axiosInstance.get('/fwd/profiles/list'),
                    axiosInstance.get('/locations/groups/list'),
                    axiosInstance.get('/locations/list'),
                    axiosInstance.get('/gateways/list'),
                ]);

                setDropdownData({
                    profiles: profilesRes.data,
                    locationGroups: locationGroupsRes.data,
                    locations: locationsRes.data,
                    gateways: gatewaysRes.data
                });

                handleChange('locationGroupId', locationGroupsRes.data[0].id)

                setLoading(false);
                clearError('fetch');
            } catch (error) {
                setError('fetch', error.message);
                setLoading(false);
            }
        };

        updateBreadcrumb([
            { label: 'Manage Forwarding Assignments', link: '/assignments' },
            { label: 'Create' },
        ]);

        fetchInitialData ();
    }, []);
    

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await axiosInstance.post('/fwd/assg/create', assignment);
            clearError('create');
            showNotification('Forwarding assignment created successfully', 'success');
            navigate('/assignments');
        } catch (error) {
            setError('create', error.message);
        }
    };

    return (
        loading ? (
            <CircularProgress />
        ) : getError('fetch') ? (
            <Typography variant="h7">{getError('fetch')}</Typography>
        ) : (
            <PageContainer>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <StyledFormTextField
                                label="Forwarding Profile"
                                select
                                value={assignment.profileId}
                                onChange={(e) => handleChange('profileId', e.target.value)}
                                fullWidth
                                required
                                margin="normal"
                            >
                                {dropdownData.profiles.map(profile => (
                                    <MenuItem key={profile.id} value={profile.id}>
                                        {profile.name}
                                    </MenuItem>
                                ))}
                            </StyledFormTextField>
                        </Grid>

                        <Grid item xs={12}>
                            <StyledFormTextField
                                label="Location Group"
                                select
                                value={assignment.locationGroupId}
                                onChange={(e) => handleChange('locationGroupId', e.target.value)}
                                fullWidth
                                required
                                margin="normal"
                                disabled
                            >
                                {dropdownData.locationGroups.map(group => (
                                    <MenuItem key={group.id} value={group.id}>
                                        {group.name}
                                    </MenuItem>
                                ))}
                            </StyledFormTextField>
                        </Grid>

                        <Grid item xs={12}>
                            <StyledFormTextField
                                label="Location"
                                select
                                value={assignment.locationId}
                                onChange={(e) => handleChange('locationId', e.target.value)}
                                fullWidth
                                required
                                margin="normal"
                            >
                                {dropdownData.locations.map(location => (
                                    <MenuItem key={location.id} value={location.id}>
                                        {location.name}
                                    </MenuItem>
                                ))}
                            </StyledFormTextField>
                        </Grid>

                        <Grid item xs={12}>
                            <StyledFormTextField
                                label="Gateway"
                                select
                                value={assignment.gatewayId}
                                onChange={(e) => handleChange('gatewayId', e.target.value)}
                                fullWidth
                                required
                                margin="normal"
                            >
                                {dropdownData.gateways.map(gateway => (
                                    <MenuItem key={gateway.id} value={gateway.id}>
                                        {gateway.name}
                                    </MenuItem>
                                ))}
                            </StyledFormTextField>
                        </Grid>
{/* 
                        <Grid item xs={12}>
                            <StyledFormTextField
                                label="Exclusive Task Type"
                                value={assignment.exclusiveTaskType}
                                onChange={(e) => handleChange('exclusiveTaskType', e.target.value)}
                                fullWidth
                                margin="normal"
                                required
                            />
                        </Grid> */}
                    </Grid>

                    <Box mt={2}>
                        <Button type="submit" variant="contained" color="primary" fullWidth>
                            Create Assignment
                        </Button>
                    </Box>

                    {getError('create') && <Typography color="error" variant="body2">{getError('create')}</Typography>}
                </form>
            </PageContainer>
        )
    );
}

export default CreateForwardingAssignment;
